import React, { useContext, useEffect, useState } from 'react'
import './index.scss'
import { StoresContext } from '@App/contexts'
import { SelectOption } from '@Components/Selectors/Select'
import { FormProvider, useForm } from 'react-hook-form'
import {
    createRightsholderFormSchema,
    CreateRightsholderQuestionnaire,
    CreateRightsholderQuestionnaireForm,
    createRightsholderQuestionnaireFormSchema,
} from '@Entities/schemas/rightsholder/create-rightsholder'
import { zodResolver } from '@hookform/resolvers/zod'
import DropdownForm from '@Fields/v2/DropdownForm'
import { FormFieldTypes } from '@App/enum/form-field-types'
import Button from '@Components/Buttons/Button'
import { ICONS } from '@Const/common'
import { observer } from 'mobx-react'
import { useNavigate } from 'react-router'
import Navigation from '@App/navigation'
import ModalPortal from '@Components/Popups/ModalPortal'
import RegistrationCompletePopup from '@Scenes/RightsholderRegistrationLayout/RegistrationCompletePopup'
import RegistrationQuestionnaireHeader from '@Scenes/RightsholderRegistrationLayout/RegistrationQuestionnaire/RegistrationQuestionnaireHeader'

const numberOfWorksOptions: SelectOption[] = [
    {
        id: 'worksOption1',
        value: 'Up to 100',
        label: 'Up to 100',
    },
    {
        id: 'worksOption2',
        value: '101 to 300',
        label: '101 to 300',
    },
    {
        id: 'worksOption3',
        value: '301 to 500',
        label: '301 to 500',
    },
    {
        id: 'worksOption4',
        value: '501 to 1,000',
        label: '501 to 1,000',
    },
    {
        id: 'worksOption5',
        value: '1,001 to 10,000',
        label: '1,001 to 10,000',
    },
    {
        id: 'worksOption6',
        value: '10,001 to 100,000',
        label: '10,001 to 100,000',
    },
    {
        id: 'worksOption7',
        value: '100,001 to 300,000',
        label: '100,001 to 300,000',
    },
    {
        id: 'worksOption8',
        value: '301,000 to 500,000',
        label: '301,000 to 500,000',
    },
    {
        id: 'worksOption9',
        value: 'Over 500,000',
        label: 'Over 500,000',
    },
]

const RegistrationQuestionnaire = () => {
    const navigate = useNavigate()
    const { createRightsholderStore, preloaderStore, userStore, popupStore } = useContext(StoresContext)
    const [popupIsOpen, setPopupIsOpen] = useState<boolean>(false)

    const validateFormIsComplete = () => {
        const newRightsholder = createRightsholderStore.createRightsholder
        try {
            createRightsholderFormSchema.parse(newRightsholder)
        } catch (ignore) {
            navigate(Navigation.registrationRightsholder.root())
        }
    }

    const onSubmit = async (data: CreateRightsholderQuestionnaireForm) => {
        preloaderStore.openTransparentPreloader(true)
        const userId = userStore.userInfo.exposedId || ''
        const questionnaire: CreateRightsholderQuestionnaire = {
            questionAnswers: [
                {
                    userId: userId,
                    questionId: 1,
                    answer: data.question1,
                },
                {
                    userId: userId,
                    questionId: 2,
                    answer: data.question2,
                },
            ],
        }
        createRightsholderStore.setCreateRightsholderQuestionnaire(questionnaire.questionAnswers)

        if (!userStore.userInfo.isInternal) {
            try {
                await createRightsholderStore.submitCreateRightsholder()
            } catch (e) {
                popupStore.setAlertFromError(e)
                navigate(Navigation.registrationRightsholder.form())
            } finally {
                userStore.changeUserSetting('showWelcomePopup', false)
                preloaderStore.openTransparentPreloader(false)
            }
            setPopupIsOpen(true)
        }
    }

    const closePopup = () => {
        navigate(Navigation.profile.rightsHolders())
        setPopupIsOpen(false)
    }

    useEffect(() => {
        validateFormIsComplete()
    }, [])

    const methods = useForm<CreateRightsholderQuestionnaireForm>({
        mode: 'onChange',
        criteriaMode: 'all',
        delayError: 750,
        resolver: zodResolver(createRightsholderQuestionnaireFormSchema),
    })

    const questions = createRightsholderStore.questions

    return (
        <>
            <RegistrationQuestionnaireHeader />

            <div className="registration-questionnaire-wrapper">
                <FormProvider {...methods}>
                    <form onSubmit={methods.handleSubmit(onSubmit)}>
                        {questions?.length && (
                            <>
                                <p>{questions[0]?.question}</p>
                                <DropdownForm
                                    options={numberOfWorksOptions}
                                    name="question1"
                                    label=""
                                    type={FormFieldTypes.DROPDOWN}
                                    className="register-dropdown"
                                />
                                <p>{questions[1]?.question}</p>
                                <DropdownForm
                                    options={numberOfWorksOptions}
                                    name="question2"
                                    label=""
                                    type={FormFieldTypes.DROPDOWN}
                                    className="register-dropdown"
                                />
                            </>
                        )}

                        <div className="registration-questionnaire-button-container">
                            <Button
                                type="secondary"
                                icon={ICONS.arrow_back}
                                mainColor
                                reverseIconPosition
                                className="back-button"
                                onClick={() => {
                                    navigate(Navigation.registrationRightsholder.form())
                                }}
                            >
                                Back
                            </Button>
                            <Button
                                type="primary"
                                icon={ICONS.arrow_forward}
                                mainColor
                                submitType="submit"
                                disabled={userStore.userInfo.isInternal}
                                className="submit-button"
                            >
                                Submit
                            </Button>
                        </div>
                    </form>
                </FormProvider>
                <ModalPortal isOpen={popupIsOpen}>
                    <RegistrationCompletePopup closePopup={closePopup} />
                </ModalPortal>
            </div>
        </>
    )
}

export default observer(RegistrationQuestionnaire)
